<section class="py-24 bg-[#0a0a0f] border-b border-white/5 relative">
	<!-- Glow effect -->
	<div
		class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 blur-[120px] rounded-full pointer-events-none"
	></div>

	<div class="container mx-auto px-6 max-w-7xl relative z-10">
		<!-- Headline -->
		<div class="text-center mb-20">
			<h2 class="text-4xl md:text-5xl font-bold text-white mb-6">
				What is Your <span class="text-gradient-primary"
					>Operating System?</span
				>
			</h2>
			<p class="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
				It's not about technology—it's about <strong
					>how your business runs</strong
				>. Your operating system is the unique way you execute daily
				operations, manage workflows, and scale your vision.
			</p>
		</div>

		<!-- Two Column Comparison -->
		<div class="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 mb-24">
			<!-- Without OS Studio -->
			<div
				class="rounded-2xl p-8 border border-red-500/20 bg-red-500/5 backdrop-blur-sm"
			>
				<h3
					class="text-2xl font-bold text-red-400 mb-8 flex items-center gap-3"
				>
					<span class="text-2xl">✕</span> Without OS Studio
				</h3>

				<ul class="space-y-6">
					<li class="flex items-start gap-4">
						<span class="text-red-500 mt-1">•</span>
						<div>
							<p class="font-bold text-gray-200 text-lg">
								Fragmented Operations
							</p>
							<p class="text-sm text-gray-400 mt-1">
								Workflows scattered across spreadsheets, emails,
								and disconnected tools
							</p>
						</div>
					</li>

					<li class="flex items-start gap-4">
						<span class="text-red-500 mt-1">•</span>
						<div>
							<p class="font-bold text-gray-200 text-lg">
								Manual Processes
							</p>
							<p class="text-sm text-gray-400 mt-1">
								Repetitive, error-prone tasks consume 30-40% of
								team time
							</p>
						</div>
					</li>

					<li class="flex items-start gap-4">
						<span class="text-red-500 mt-1">•</span>
						<div>
							<p class="font-bold text-gray-200 text-lg">
								Slow Scaling
							</p>
							<p class="text-sm text-gray-400 mt-1">
								Each new team member requires months to
								understand your way of working
							</p>
						</div>
					</li>

					<li class="flex items-start gap-4">
						<span class="text-red-500 mt-1">•</span>
						<div>
							<p class="font-bold text-gray-200 text-lg">
								Institutional Risk
							</p>
							<p class="text-sm text-gray-400 mt-1">
								Knowledge lives in people's heads, not systems.
								Turnover creates chaos
							</p>
						</div>
					</li>
				</ul>
			</div>

			<!-- With OS Studio -->
			<div
				class="rounded-2xl p-8 border border-primary/30 bg-primary/5 backdrop-blur-sm relative overflow-hidden group"
			>
				<div
					class="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"
				></div>

				<h3
					class="text-2xl font-bold text-white mb-8 flex items-center gap-3 relative"
				>
					<span class="text-2xl text-neon-cyan">✓</span> With OS Studio
				</h3>

				<ul class="space-y-6 relative">
					<li class="flex items-start gap-4">
						<span class="text-neon-cyan font-bold mt-1 text-xl"
							>→</span
						>
						<div>
							<p class="font-bold text-white text-lg">
								Unified Operating System
							</p>
							<p class="text-sm text-gray-400 mt-1">
								All workflows, processes, and data in one
								coherent, intelligent system
							</p>
						</div>
					</li>

					<li class="flex items-start gap-4">
						<span class="text-neon-cyan font-bold mt-1 text-xl"
							>→</span
						>
						<div>
							<p class="font-bold text-white text-lg">
								Automated Excellence
							</p>
							<p class="text-sm text-gray-400 mt-1">
								Repetitive tasks run 24/7. Your team focuses on
								strategy and growth
							</p>
						</div>
					</li>

					<li class="flex items-start gap-4">
						<span class="text-neon-cyan font-bold mt-1 text-xl"
							>→</span
						>
						<div>
							<p class="font-bold text-white text-lg">
								Frictionless Scaling
							</p>
							<p class="text-sm text-gray-400 mt-1">
								New team members onboard in days. Your OS trains
								them through intelligent systems
							</p>
						</div>
					</li>

					<li class="flex items-start gap-4">
						<span class="text-neon-cyan font-bold mt-1 text-xl"
							>→</span
						>
						<div>
							<p class="font-bold text-white text-lg">
								Operational Resilience
							</p>
							<p class="text-sm text-gray-400 mt-1">
								Processes codified and automated. No single
								point of failure or knowledge gap
							</p>
						</div>
					</li>
				</ul>
			</div>
		</div>

		<!-- Core Concepts -->
		<div
			class="relative rounded-3xl p-12 overflow-hidden border border-white/10 bg-[#13131f]/50 backdrop-blur-md"
		>
			<!-- Background Grid -->
			<div
				class="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:30px_30px]"
			></div>

			<h3
				class="relative text-3xl font-bold mb-12 text-center text-white"
			>
				Your Business Operating System Includes
			</h3>

			<div class="relative grid grid-cols-1 md:grid-cols-3 gap-10">
				<div class="group">
					<div
						class="text-4xl mb-4 transform group-hover:scale-110 transition-transform duration-300"
					>
						⚙️
					</div>
					<h4 class="text-xl font-bold mb-3 text-white">
						Core Processes
					</h4>
					<p class="text-gray-400 leading-relaxed">
						The fundamental workflows that define how work gets
						done: sales, delivery, support, operations, compliance.
					</p>
				</div>

				<div class="group">
					<div
						class="text-4xl mb-4 transform group-hover:scale-110 transition-transform duration-300"
					>
						🔗
					</div>
					<h4 class="text-xl font-bold mb-3 text-white">
						Integrated Systems
					</h4>
					<p class="text-gray-400 leading-relaxed">
						All your tools working as one ecosystem: CRM, ERP,
						project management, analytics, and more—perfectly
						synchronized.
					</p>
				</div>

				<div class="group">
					<div
						class="text-4xl mb-4 transform group-hover:scale-110 transition-transform duration-300"
					>
						📊
					</div>
					<h4 class="text-xl font-bold mb-3 text-white">
						Intelligence & Insights
					</h4>
					<p class="text-gray-400 leading-relaxed">
						Real-time data flows through your system. Dashboards and
						alerts keep everyone aligned on what matters most.
					</p>
				</div>

				<div class="group">
					<div
						class="text-4xl mb-4 transform group-hover:scale-110 transition-transform duration-300"
					>
						🎯
					</div>
					<h4 class="text-xl font-bold mb-3 text-white">
						Strategic Execution
					</h4>
					<p class="text-gray-400 leading-relaxed">
						Your strategy becomes operational. Goals cascade through
						systems, metrics track progress, AI optimizes execution.
					</p>
				</div>

				<div class="group">
					<div
						class="text-4xl mb-4 transform group-hover:scale-110 transition-transform duration-300"
					>
						🛡️
					</div>
					<h4 class="text-xl font-bold mb-3 text-white">
						Governance & Control
					</h4>
					<p class="text-gray-400 leading-relaxed">
						Built-in compliance, risk management, audit trails, and
						security. Operate with confidence and transparency.
					</p>
				</div>

				<div class="group">
					<div
						class="text-4xl mb-4 transform group-hover:scale-110 transition-transform duration-300"
					>
						🚀
					</div>
					<h4 class="text-xl font-bold mb-3 text-white">
						Continuous Evolution
					</h4>
					<p class="text-gray-400 leading-relaxed">
						As your business changes, your OS adapts. Add
						capabilities, scale teams, expand into new markets
						effortlessly.
					</p>
				</div>
			</div>
		</div>

		<!-- Value Proposition -->
		<div class="text-center mt-24">
			<h3 class="text-3xl font-bold text-white mb-6">
				Built for Your <span class="text-primary italic">Unique</span> Business
			</h3>
			<p
				class="text-xl text-gray-400 max-w-3xl mx-auto mb-10 leading-relaxed"
			>
				We don't give you a generic platform. We build your operating
				system based on <strong>your</strong> processes,
				<strong>your</strong> culture, and <strong>your</strong> vision.
				Every organization runs differently—and that's exactly what OS Studio
				is designed for.
			</p>

			<div class="flex flex-col sm:flex-row gap-6 justify-center">
				<a
					href="/start"
					class="px-8 py-4 bg-primary text-white rounded-xl font-bold hover:bg-primary-dark transition-all shadow-lg hover:shadow-[0_0_30px_rgba(124,58,237,0.4)]"
				>
					Describe Your Unique OS
				</a>
				<a
					href="/contact"
					class="px-8 py-4 border border-white/20 bg-white/5 text-white rounded-xl font-bold hover:bg-white/10 hover:border-white/40 transition-all"
				>
					Schedule a Consultation
				</a>
			</div>
		</div>
	</div>
</section>

<style>
	section {
		scroll-behavior: smooth;
	}
</style>
